import React, { useState } from 'react';
import { Alert, Button, Checkbox, Empty, Input, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import { RegulationReaderBody } from './RegulationViews';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { userMessageForError } from '../../utils/apiError';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';
import { topicFacets } from '../../utils/regulatoryAdminModel';
import { PAGE_SIZE, authorityOptions, buildListQuery, documentTypeLabel, documentTypeOptions, sinceInvalid } from '../../utils/regulatoryModel';

const { Text } = Typography;

const Detail = ({ id, onClose, onOpen }) => {
    const { request } = useApiRequest();
    const detail = useResource(async () => (await request('GET', `/regulatory-content/${id}`)).data.item, [request, id]);
    return (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 16, marginTop: 16 }} data-testid="library-detail">
            {detail.status === 'loading' && !detail.data && <Spin />}
            {detail.status === 'error' && <Alert type="error" message={userMessageForError(detail.error)} action={<Button onClick={detail.reload}>Retry</Button>} />}
            {detail.data && (
                <>
                    <Space style={{ marginBottom: 8 }}>
                        <Tag>{detail.data.authorityName}</Tag>
                        <Tag>{documentTypeLabel(detail.data.documentType)}</Tag>
                        <Text strong>{detail.data.title}</Text>
                        <Button onClick={onClose}>Close</Button>
                    </Space>
                    <RegulationReaderBody item={detail.data} onOpen={onOpen} />
                </>
            )}
        </div>
    );
};

/** Regulatory library for Lead Advisor / Advisor / Company SPOC: published items only (the API enforces it). */
const RegulatoryLibraryView = () => {
    const { request } = useApiRequest();
    const [draftFilters, setDraftFilters] = useState({});
    const [filters, setFilters] = useState({});
    const [offset, setOffset] = useState(0);
    const [selectedId, setSelectedId] = useState(null);

    const list = useResource(
        async () => request('GET', '/regulatory-content', { query: buildListQuery(filters, { offset }) }),
        [request, filters, offset],
    );
    // Readers have no taxonomy route: offer only the domains/topics that published items actually carry.
    const facets = useResource(async () => topicFacets((await request('GET', '/regulatory-content', { query: { limit: 200 } })).data.items), [request]);
    const set = (k) => (v) => setDraftFilters((f) => ({ ...f, [k]: v }));
    const sinceBad = sinceInvalid(draftFilters.since);
    const items = list.data?.data?.items || [];
    const total = list.data?.meta?.total ?? items.length;

    const columns = [
        { title: 'Title', key: 'title', render: (_v, r) => <Text>{r.title}</Text> },
        { title: 'Authority', key: 'authority', render: (_v, r) => r.authorityName },
        { title: 'Type', key: 'type', render: (_v, r) => documentTypeLabel(r.documentType) },
        { title: 'Reference', key: 'ref', render: (_v, r) => r.officialDocumentNumber || '-' },
        { title: 'Issued', key: 'issued', render: (_v, r) => formatDdMmYyyy(r.publishedAt) },
        { title: '', key: 'open', render: (_v, r) => <Button onClick={() => setSelectedId(r.id)}>View</Button> },
    ];

    return (
        <div>
            <Space wrap style={{ marginBottom: 12 }}>
                <Select aria-label="Authority filter" style={{ width: 140 }} placeholder="Authority" allowClear value={draftFilters.authority} onChange={set('authority')} options={authorityOptions()} />
                <Select aria-label="Type filter" style={{ width: 180 }} placeholder="Document type" allowClear value={draftFilters.documentType} onChange={set('documentType')} options={documentTypeOptions()} />
                <Select aria-label="Domain filter" style={{ width: 200 }} placeholder="Domain" allowClear value={draftFilters.domain} onChange={set('domain')} options={facets.data?.domains || []} />
                <Select aria-label="Topic filter" style={{ width: 220 }} placeholder="Topic" allowClear showSearch optionFilterProp="label" value={draftFilters.topic} onChange={set('topic')} options={facets.data?.topics || []} />
                <Input aria-label="Search" style={{ width: 200 }} placeholder="Search title or reference" value={draftFilters.q || ''} onChange={(e) => set('q')(e.target.value)} />
                <Input aria-label="Issued since" style={{ width: 160 }} placeholder="Since (DD-MM-YYYY)" value={draftFilters.since || ''} onChange={(e) => set('since')(e.target.value)} />
                <Checkbox checked={!!draftFilters.includeSuperseded} onChange={(e) => set('includeSuperseded')(e.target.checked)}>Include superseded</Checkbox>
                <Button disabled={sinceBad} onClick={() => { setOffset(0); setFilters(draftFilters); }}>Apply filters</Button>
            </Space>
            {sinceBad && <div><Text type="danger">Enter the date as DD-MM-YYYY.</Text></div>}

            {list.status === 'loading' && !list.data && <Spin />}
            {list.status === 'error' && <Alert type="error" message={userMessageForError(list.error)} action={<Button onClick={list.reload}>Retry</Button>} />}
            {list.data && items.length === 0 && <Empty description="No published regulations match these filters." />}
            {list.data && items.length > 0 && (
                <>
                    <Table columns={columns} dataSource={items} rowKey={(r) => r.id} pagination={false} size="small" />
                    <Space style={{ marginTop: 8 }}>
                        <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
                        <Text type="secondary">{offset + 1}-{offset + items.length} of {total}</Text>
                        <Button disabled={offset + items.length >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
                    </Space>
                </>
            )}
            {selectedId && <Detail key={selectedId} id={selectedId} onClose={() => setSelectedId(null)} onOpen={setSelectedId} />}
        </div>
    );
};

export default RegulatoryLibraryView;
